<template>
  <div class="bills-registry-main">
    <loader
      class="bills-registry-main__loader"
      v-if="isRegistryProcessing || isRegistryPositionDeleting"
    ></loader>
    <transactions-page-controls
      :has-filter="false"
      :has-export="false"
      :stats="registryStats"
      :export-options="[{ id: 'xlsx', name: 'export-action.xlsx' }]"
      @refresh="handleRefresh"
      @export="handleExport"
      :is-show-statistic-block-by-default="false"
    >
    </transactions-page-controls>
    <bills-registry-main-table
      class="bills-registry-main__table"
      :bills-registry-items="billsRegistry || []"
      @repeatPayoutsRegistryItem="repeatPayoutsRegistryItem"
    ></bills-registry-main-table>
    <success-notification
      ref="payoutsRegistryProcessingNotification"
      :text="
        $t('payouts.by-registry.created.notifications.registry-in-progress')
      "
    ></success-notification>
  </div>
</template>

<script>
import { $on, $off, $once, $emit } from '../../../../utils/gogocodeTransfer'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import TransactionsPageControls from '@vue-app/components/company/transactions/shared/transactions-page-controls/TransactionsPageControls.vue'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import { Stats } from '@js-app/entities/transaction/Stats'
import TransactionsTable from '@vue-app/components/company/transactions/table/TransactionsTable.vue'
import PayoutsRegistryCreatedTable from '@vue-app/components/company/payouts/by-registry/table/PayoutsRegistryCreatedTable.vue'
import { mapState } from 'vuex'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import PayoutsRegistryItemDeletePopup from '@vue-app/components/company/payouts/by-registry/popup/PayoutsRegistryItemDeletePopup.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import Loader from '@vue-app/components/base/Loader.vue'
import PayoutsRegistryMainTable from '@vue-app/components/company/payouts/by-registry/table/PayoutsRegistryMainTable.vue'
import BillsRegistryMainTable from '@vue-app/components/company/bills/by-registry/main/table/BillsRegistryMainTable.vue'
import { formatCoinsToText } from '@js-app/services/helpers/numbers'
import { downloadBase64File } from '@js-app/services/helpers/download'

export default {
  name: 'BillsRegistryMain',
  components: {
    PayoutsRegistryMainTable,
    Loader,
    SuccessNotification,
    PayoutsRegistryItemDeletePopup,
    PayoutsRegistryCreatedTable,
    TransactionsTable,
    RequireAccess,
    TransactionsPageControls,
    PrimaryButton,
    BillsRegistryMainTable,
  },
  props: {
    billsRegistry: {
      type: [Object, Array],
    },
    billsRegistryPositions: {
      type: [Object, Array],
    },
  },
  data() {
    return {
      billsRegistryPositionsList: [],
      isRegistryPositionDeleting: false,
      isRegistryProcessing: false,
      isExportRunning: false,
    }
  },
  computed: {
    ...mapState(['selectedCompany']),
    registryStats() {
      return new Stats({
        OperationsStats: {
          All: {
            Sum:
              this.billsRegistryPositions
                .map((val) => {
                  return val.sum || 0
                })
                .reduce((total, val) => total + val, 0) * 100,
            Count: this.billsRegistry.total_count,
          },
        },
      })
    },
    registryCommission() {
      return formatCoinsToText(
        this.billsRegistry.registry_stat.total_commission
      )
    },
    registryProgressStats() {
      return {
        processing: {
          count: this.billsRegistry.total_count,
          sum: `${this.billsRegistry.total_sum} ₴`,
          percents: (
            (this.billsRegistry.total_count /
              this.registryStats.getPlainStats.count) *
            100
          ).toFixed(0),
        },
        completed: {
          count: this.billsRegistry.total_count,
          sum: `${this.billsRegistry.total_sum} ₴`,
          percents: (
            this.billsRegistry.total_count /
            this.registryStats.getPlainStats().count
          ).toFixed(0),
        },
        error: {
          count: this.billsRegistry.total_count,
          sum: `${this.billsRegistry.total_sum} ₴`,
          percents: (
            (this.billsRegistry.total_count /
              this.registryStats.getPlainStats.count) *
            100
          ).toFixed(0),
        },
      }
    },
  },
  created() {
    this.billsRegistryPositionsList = this.billsRegistryPositions || []
  },
  methods: {
    async repeatPayoutsRegistryItem(payoutsRegistryItem) {
      if (this.isRegistryProcessing) {
        return
      }

      this.isRegistryProcessing = true

      try {
        await CompaniesApi.repeatPayoutsRegistryItem(
          this.selectedCompany,
          this.payoutsRegistry.id,
          payoutsRegistryItem.id
        )
      } finally {
        this.isRegistryProcessing = false
      }
    },
    async handleExport(exportType) {
      if (this.isExportRunning) {
        return
      }

      this.isExportRunning = true
      try {
        let exportResponse = await CompaniesApi.exportPayoutsRegistry(
          this.selectedCompany,
          this.billsRegistry.id
        )

        downloadBase64File(
          exportResponse.data.FileBase64,
          `bills-registry-${this.billsRegistry.id}.${exportType.id}`,
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        )
      } catch (e) {
        console.error(e)
      } finally {
        this.isExportRunning = false
      }
    },
    handleRefresh() {
      $emit(this, 'refresh')
    },
  },
  emits: ['refresh'],
}
</script>

<style lang="scss" scoped>
.bills-registry-main {
  position: relative;
  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99;
  }

  &__table {
    margin-top: 21px;
  }

  &__stats {
    margin-top: 15px;
    padding: 15px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 30px;
    background: #ffffff;
    border-radius: 20px;

    &-details {
      border: 1px solid #eff2f8;
      border-radius: 10px;

      &-item {
        display: grid;
        grid-template-columns: 1fr 1fr;
        border-bottom: 1px solid #eff2f8;

        &:last-child {
          border-bottom: none;
        }

        &-title {
          font-weight: 500;
          font-size: 12px;
          line-height: 15px;
          color: #67768a;
          padding: 19px 40px 22px 40px;
          border-right: 1px solid #eff2f8;
          display: flex;
          align-items: center;
        }

        &-value {
          padding: 19px 33px 22px 33px;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          color: #585f6d;
        }
      }
    }

    &-progress {
      padding: 20px 40px;

      &-item {
        display: flex;
        align-items: flex-end;
        margin-bottom: 15px;

        &:last-child {
          margin-bottom: 0;
        }

        &--completed & {
          &-title {
            &-text,
            &-value {
              color: #83c382;
            }
          }

          &-row {
            background: #ccf7cb;

            &-filled {
              background: #83c482;
            }
          }
        }

        &--processing & {
          &-title {
            &-text,
            &-value {
              color: #71a2dd;
            }
          }

          &-row {
            background: #dbe7fd;

            &-filled {
              background: #71a2dd;
            }
          }
        }

        &--error & {
          &-title {
            &-text,
            &-value {
              color: #f56767;
            }
          }

          &-row {
            background: #fdd9d9;

            &-filled {
              background: #f56767;
            }
          }
        }

        &-title {
          display: flex;
          align-items: center;
          margin-bottom: 3px;

          &-text {
            font-weight: 500;
            font-size: 12px;
            line-height: 28px;
            margin-right: 19px;
          }

          &-value {
            font-weight: 600;
            font-size: 14px;
            line-height: 20px;
          }
        }

        &-bar {
          width: 308px;
          margin-right: 25px;
        }

        &-row {
          position: relative;
          border-radius: 15px;
          width: 100%;
          height: 20px;
          overflow: hidden;

          &-filled {
            position: absolute;
            height: 100%;
            left: 0;
            top: 0;
          }

          &-percents {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            font-weight: 500;
            font-size: 12px;
            line-height: 20px;
            color: #ffffff;

            &.left {
              left: calc(100% + 6px);
            }

            &.right {
              right: 8px;
            }
          }
        }

        &-amount {
          font-weight: 600;
          font-size: 14px;
          line-height: 20px;
          color: #585f6d;
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
